import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Barcode from "react-barcode";
import { api } from "../../api/client";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Prefer a dedicated image base (S3). Falls back to the known S3 if env not set.
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

// Normalize "catalog/..." keys and build a usable URL for <img src>
const normalizeKey = (v = "") =>
  typeof v === "string"
    ? v.replace(/^\/+/, "").replace(/^catalog\//i, "catalog/")
    : "";

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveImageUrl = (raw) => {
  if (!raw) return "";
  // if response ever returns an array of images, pick the first
  const val = Array.isArray(raw) ? raw[0] : raw;

  // keep data URLs as-is
  if (isDataUrl(val)) return val;

  // already absolute
  if (isAbsoluteHttp(val)) return val;

  // otherwise it's a key like "catalog/IMG_....jpeg"
  const key = normalizeKey(val);
  return `${IMAGE_BASE}/${key}`;
};

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const lang = localStorage.getItem("lang") || "en";

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(
          `${API_BASE_URL}api/products/product-detail/${id}`,
          {
            headers: { "Accept-Language": lang },
          }
        );
        setProduct(res.data.data);
      } catch (err) {
        console.error("Failed to fetch product:", err);
      }
    };

    fetchProduct();
  }, [id, lang]);

  if (!product) return <div>Loading...</div>;

  // Support either `image`, `images`, or nested fields just in case
  const rawImage =
    product.image ??
    product.images ??
    product.product?.image ??
    product.product?.images ??
    "";

  const imageSrc = resolveImageUrl(rawImage);

  const getImageName = (src) => {
    if (!src) return "product.jpg";
    if (isDataUrl(src)) return "base64-image";
    try {
      const url = new URL(src);
      return url.pathname.split("/").pop() || "product.jpg";
    } catch {
      // not a full URL; treat as key
      return String(src).split("/").pop() || "product.jpg";
    }
  };

  const getImageSizeKB = (src) => {
    if (!isDataUrl(src)) return null;
    return ((src.length * 3) / 4 / 1024).toFixed(0);
  };

  const formatLabel = (text) => {
    if (!text) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>{window.translate("Product Details")}</h4>
            <h6>{window.translate("Full details of a product")}</h6>
          </div>
        </div>

        <div className="row">
          {/* Left - Details */}
          <div className="col-lg-8 col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="bar-code-view">
                  {product.sku && <Barcode value={product.sku} />}
                  <a className="printimg" onClick={() => window.print()}>
                    <img src="/assets/img/icons/printer.svg" alt="print" />
                  </a>
                </div>

                <div className="productdetails">
                  <ul className="product-bar">
                    <li>
                      <h4>{window.translate("Product")}</h4>
                      <h6>{product.name}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Category")}</h4>
                      <h6>{product.category}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Sub Category")}</h4>
                      <h6>{product.subcategory}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Brand")}</h4>
                      <h6>{product.brand}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Unit")}</h4>
                      <h6>{product.unit}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("SKU")}</h4>
                      <h6>{product.sku}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Quantity")}</h4>
                      <h6>{product.quantity}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Low Stock Alert")}</h4>
                      <h6>{product.quantityAlert}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Tax Type")}</h4>
                      <h6>{formatLabel(product.taxType)}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Discount Type")}</h4>
                      <h6>{formatLabel(product.discountType)}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Discount Value")}</h4>
                      <h6>{product.discountValue ?? 0}%</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Price")}</h4>
                      <h6>{(product.price ?? 0).toFixed(2)}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Status")}</h4>
                      <h6>{product.status}</h6>
                    </li>
                    <li>
                      <h4>{window.translate("Description")}</h4>
                      <h6>{product.description}</h6>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="col-lg-4 col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="slider-product-details">
                  <div className="product-slide">
                    <div className="slider-product">
                      <img
                        src={imageSrc}
                        alt="product"
                        style={{ width: "100%", borderRadius: "8px" }}
                        onError={(e) => {
                          // Fallback if something goes wrong
                          e.currentTarget.src = "/assets/img/product-placeholder.png";
                        }}
                      />
                      <h4>{getImageName(imageSrc)}</h4>
                      {isDataUrl(imageSrc) && (
                        <h6>{getImageSizeKB(imageSrc)} kb</h6>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /Right - Image */}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
